import { useEffect, useState } from 'react';

import { usePersistedState } from './usePersistedState';

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * A persisted search box: the raw text the user is typing, debounced into a
 * second value meant to key a data fetch. Both start from localStorage on
 * mount, so a restored search term is already in its debounced form on the
 * first render — the first fetch a page makes already carries it, rather
 * than firing once empty and again once the debounce catches up.
 */
export const usePersistedSearch = (key: string, debounceMs: number = DEFAULT_DEBOUNCE_MS) => {
  const [searchText, setSearchText] = usePersistedState(key, '');
  const [debouncedSearchText, setDebouncedSearchText] = useState(() => searchText.trim());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText.trim()), debounceMs);
    return () => clearTimeout(timer);
  }, [searchText, debounceMs]);

  const clearSearch = () => {
    setSearchText('');
    setDebouncedSearchText('');
  };

  return { searchText, setSearchText, debouncedSearchText, clearSearch };
};
